import { useState, useEffect, useCallback } from 'react';
import { ProjectData } from '../components/pages/ProjectDetail/projectData';
import { fetchAllProjects, fetchProjectList } from '../services/notionClient';
import { useImagePreloader } from './useImagePreloader';

// グローバルキャッシュ（セッション中保持）
let projectListCache: Array<{
  id: string;
  title: string;
  category: string;
  image: string;
}> | null = null;

// 全プロジェクト取得（画像プリロード付き）
export const useAllProjectsWithPreload = () => {
  const [projects, setProjects] = useState<Record<string, ProjectData>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { preloadProjectImages, isImageLoaded, loadedImages } = useImagePreloader();

  useEffect(() => {
    const loadProjects = async () => {
      try {
        setLoading(true);
        const data = await fetchAllProjects();
        setProjects(data);
        
        // プロジェクトデータからサムネイル画像をプリロード
        const projectArray = Object.values(data);
        await preloadProjectImages(projectArray);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 依存配列を空にして、初回のみ実行

  return { projects, loading, error, isImageLoaded, loadedImages };
};

// プロジェクト一覧（軽量版）取得（画像プリロード付き）
export const useProjectListWithPreload = () => {
  const [projects, setProjects] = useState<Array<{
    id: string;
    title: string;
    category: string;
    image: string;
  }>>(projectListCache || []);
  const [loading, setLoading] = useState(!projectListCache);
  const [error, setError] = useState<Error | null>(null);
  const { preloadImages, isImageLoaded, isPreloading } = useImagePreloader();

  useEffect(() => {
    // キャッシュがあればスキップ
    if (projectListCache) {
      console.log('✅ Using cached project list');
      return;
    }

    const loadProjectList = async () => {
      try {
        setLoading(true);
        console.log('🔄 Fetching project list from API...');
        const data = await fetchProjectList();
        setProjects(data);
        projectListCache = data; // キャッシュに保存

        // サムネイル画像をプリロード
        const imageUrls = data.map((project: any) => project.image).filter(Boolean);
        await preloadImages(imageUrls);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    loadProjectList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 依存配列を空にして、初回のみ実行

  return { projects, loading, error, isImageLoaded, isPreloading };
};

// プロジェクトホバー時の詳細画像プリフェッチ
export const useProjectPrefetch = () => {
  const { preloadImages } = useImagePreloader();
  const [prefetchingProjectId, setPrefetchingProjectId] = useState<string | null>(null);

  const prefetchProjectImages = useCallback(async (project: ProjectData) => {
    if (!project.images || project.images.length <= 1) return;
    
    setPrefetchingProjectId(project.title);
    
    // 最初の3枚の画像をプリフェッチ（1枚目はすでに読み込まれているはず）
    const imagesToPrefetch = project.images.slice(1, 4);
    await preloadImages(imagesToPrefetch);
    
    setPrefetchingProjectId(null);
  }, [preloadImages]);

  return { prefetchProjectImages, prefetchingProjectId };
};